import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

/**
 * Coordina la pantalla de carga del store.
 *
 * Los componentes que traen datos al entrar registran su trabajo con
 * beginTask(); la pantalla se va cuando todos terminaron, no con un
 * temporizador a ciegas.
 */

const StoreLoadingContext = createContext(null);

// La pantalla es solo para la primera entrada al store, no para cada
// navegacion interna. Se marca al ocultarse y no al montar, para que
// StrictMode (que monta dos veces en dev) no se la coma.
let yaMostrada = false;

const MIN_VISIBLE_MS = 700;   // evita el parpadeo cuando la red responde al instante
const SIN_TAREAS_MS = 1200;   // nadie pidio datos: no dejamos la pantalla colgada
const MAX_VISIBLE_MS = 7000;  // red de seguridad si una peticion nunca resuelve

const NOOP = () => () => {};
const SIN_PROVIDER = { visible: false, beginTask: NOOP };

export function StoreLoadingProvider({ children }) {
  const [visible, setVisible] = useState(() => !yaMostrada);
  const [pendientes, setPendientes] = useState(0);
  const [arranco, setArranco] = useState(false);
  const inicioRef = useRef(Date.now());

  const ocultar = useCallback(() => {
    yaMostrada = true;
    setVisible(false);
  }, []);

  const beginTask = useCallback(() => {
    setArranco(true);
    setPendientes((n) => n + 1);

    let terminada = false;
    return () => {
      if (terminada) return;
      terminada = true;
      setPendientes((n) => Math.max(0, n - 1));
    };
  }, []);

  // Camino normal: arrancaron las cargas y ya no queda ninguna pendiente.
  useEffect(() => {
    if (!visible || !arranco || pendientes > 0) return;

    const transcurrido = Date.now() - inicioRef.current;
    const t = setTimeout(ocultar, Math.max(0, MIN_VISIBLE_MS - transcurrido));
    return () => clearTimeout(t);
  }, [visible, arranco, pendientes, ocultar]);

  // Nadie registro tareas (ej. se entra directo al carrito).
  useEffect(() => {
    if (!visible || arranco) return;
    const t = setTimeout(ocultar, SIN_TAREAS_MS);
    return () => clearTimeout(t);
  }, [visible, arranco, ocultar]);

  // Tope duro: pase lo que pase, la pantalla se va.
  useEffect(() => {
    if (!visible) return;
    const t = setTimeout(ocultar, MAX_VISIBLE_MS);
    return () => clearTimeout(t);
  }, [visible, ocultar]);

  return (
    <StoreLoadingContext.Provider value={{ visible, beginTask }}>
      {children}
    </StoreLoadingContext.Provider>
  );
}

export function useStoreLoading() {
  return useContext(StoreLoadingContext) ?? SIN_PROVIDER;
}
